import posthog from "posthog-js";

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";
const UI_HOST =
	import.meta.env.VITE_POSTHOG_UI_HOST || "https://us.posthog.com";
const RELEASE = import.meta.env.VITE_GIT_SHA;

let initialized = false;

export function initPostHog(): void {
	if (initialized) return;
	if (!KEY) return;
	posthog.init(KEY, {
		api_host: HOST,
		ui_host: UI_HOST,
		person_profiles: "identified_only",
		capture_pageview: false,
		capture_pageleave: true,
		autocapture: true,
	});
	if (RELEASE) posthog.register({ $release: RELEASE });
	initialized = true;
}

function client(): typeof posthog | null {
	return initialized ? posthog : null;
}

type IdentifyArgs = {
	userId: string;
	email?: string | null;
	name?: string | null;
	activeOrganizationId?: string | null;
	tenantId?: string | null;
};

export function identifyUser(args: IdentifyArgs): void {
	const ph = client();
	if (!ph) return;
	const properties: Record<string, unknown> = {};
	if (args.email) properties.email = args.email;
	if (args.name) properties.name = args.name;
	if (args.activeOrganizationId)
		properties.activeOrganizationId = args.activeOrganizationId;
	if (args.tenantId) properties.tenantId = args.tenantId;
	ph.identify(args.userId, properties);
}

export function resetUser(): void {
	const ph = client();
	if (!ph) return;
	ph.reset();
}

export function capturePageview(path: string): void {
	const ph = client();
	if (!ph) return;
	ph.capture("$pageview", {
		$current_url: window.location.href,
		pathname: path,
	});
}

export function captureEvent(
	event: string,
	properties?: Record<string, unknown>,
): void {
	const ph = client();
	if (!ph) return;
	ph.capture(event, properties);
}

/**
 * Current PostHog session id, forwarded to server actions so `$ai_*` events
 * link back to the recording. Undefined if uninitialised or not yet booted.
 */
export function getSessionId(): string | undefined {
	const ph = client();
	if (!ph) return undefined;
	const id = ph.get_session_id();
	return typeof id === "string" && id.length > 0 ? id : undefined;
}
